import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet,
  Lock,
  Printer,
  ChevronRight,
  TrendingUp,
  Clock,
  RotateCcw,
  AlertCircle,
  Scale,
  CheckCircle2,
} from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { YearSelector } from '@/components/reports/YearSelector';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { FilterBar } from '@/components/common/FilterBar';
import { Combobox } from '@/components/common/Combobox';
import { StatusBadge } from '@/components/common/StatusBadge';
import { LoadingState } from '@/components/common/LoadingState';
import { Button } from '@/components/ui/button';
import { settlementService } from '@/services/settlementService';
import { farmService } from '@/services/farmService';
import { cycleService } from '@/services/cycleService';
import { SettlementRecord, ProductionCycleRecord, FarmRecord, GrowerRecord } from '@/types/database';
import { formatCurrency, formatDate } from '@/lib/utils';

export const SettlementsPage: React.FC = () => {
  const navigate = useNavigate();

  const [settlements, setSettlements] = useState<SettlementRecord[]>([]);
  const [readyCycles, setReadyCycles] = useState<ProductionCycleRecord[]>([]);
  const [farms, setFarms] = useState<FarmRecord[]>([]);
  const [growers, setGrowers] = useState<GrowerRecord[]>([]);

  // Filter state
  const [selectedYear, setSelectedYear] = useState<string>('2026');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedGrowerId, setSelectedGrowerId] = useState<string>('');
  const [selectedFarmId, setSelectedFarmId] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settlementsData, cyclesData, farmsData, growersData] = await Promise.all([
        settlementService.getSettlements({ status: 'all' }),
        settlementService.getCyclesReadyToSettle(),
        farmService.getActiveFarms(),
        cycleService.getGrowersWithActiveContracts(),
      ]);
      setSettlements(settlementsData);
      setReadyCycles(cyclesData);
      setFarms(farmsData);
      setGrowers(growersData);
    } catch (err: any) {
      console.error('Error loading settlements:', err.message);
      setError(err.message || 'Failed to load settlements from database.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Helper strictly implementing the locked rule: Associated Cycle Batch Year derived from batch_code
  const getSettlementBatchYear = useCallback((s: SettlementRecord): string | null => {
    const batchCode = s.production_cycles?.batch_code;
    if (!batchCode) return null;
    const match = batchCode.match(/\b(20\d{2})\b/);
    return match ? match[1] : null;
  }, []);

  const getCycleBatchYear = useCallback((c: ProductionCycleRecord): string | null => {
    if (!c.batch_code) return null;
    const match = c.batch_code.match(/\b(20\d{2})\b/);
    return match ? match[1] : null;
  }, []);

  // Derive available batch years dynamically from settlement & cycle records
  const availableYears = useMemo(() => {
    const yearsSet = new Set<string>();
    settlements.forEach((s) => {
      const yr = getSettlementBatchYear(s);
      if (yr) yearsSet.add(yr);
    });
    readyCycles.forEach((c) => {
      const yr = getCycleBatchYear(c);
      if (yr) yearsSet.add(yr);
    });

    if (yearsSet.size === 0) {
      yearsSet.add('2026');
      yearsSet.add('2025');
      yearsSet.add('2024');
    }

    return Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
  }, [settlements, readyCycles, getSettlementBatchYear, getCycleBatchYear]);

  // Synchronize default year
  useEffect(() => {
    if (availableYears.length > 0 && selectedYear !== 'all' && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // 1. Base records filtered by Associated Cycle Batch Year
  const yearSettlements = useMemo(() => {
    if (selectedYear === 'all') return settlements;
    return settlements.filter((s) => getSettlementBatchYear(s) === selectedYear);
  }, [settlements, selectedYear, getSettlementBatchYear]);

  const yearReadyCycles = useMemo(() => {
    const activeSettlementCycleIds = new Set(settlements.filter((s) => !s.is_void).map((s) => s.cycle_id));
    const unstarted = readyCycles.filter((c) => !activeSettlementCycleIds.has(c.id));
    if (selectedYear === 'all') return unstarted;
    return unstarted.filter((c) => getCycleBatchYear(c) === selectedYear);
  }, [readyCycles, settlements, selectedYear, getCycleBatchYear]);

  // 2. Further filtered settlements for display
  const filteredSettlements = useMemo(() => {
    return yearSettlements.filter((s) => {
      // Status filter
      if (statusFilter !== 'all' && statusFilter !== 'active' && statusFilter !== 'ready_to_settle') {
        if (statusFilter === 'draft') {
          if (s.status !== 'draft' && s.status !== 'approved') return false;
        } else if (statusFilter === 'finalized') {
          if (s.status !== 'frozen' && (s.status as string) !== 'finalized') return false;
        } else if ((s.status as string) !== statusFilter) {
          return false;
        }
      }

      // Grower filter
      if (selectedGrowerId && s.grower_id !== selectedGrowerId) {
        return false;
      }

      // Farm filter
      if (selectedFarmId && s.production_cycles?.farms?.id !== selectedFarmId) {
        return false;
      }

      // Search term
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const settlementNo = (s.settlement_number || '').toLowerCase();
        const cycleCode = (s.production_cycles?.cycle_code || '').toLowerCase();
        const batchCode = (s.production_cycles?.batch_code || '').toLowerCase();
        const growerName = (s.growers?.full_name || s.legacy_raiser_name || '').toLowerCase();
        const farmName = (s.production_cycles?.farms?.farm_name || '').toLowerCase();

        return (
          settlementNo.includes(term) ||
          cycleCode.includes(term) ||
          batchCode.includes(term) ||
          growerName.includes(term) ||
          farmName.includes(term)
        );
      }

      return true;
    });
  }, [yearSettlements, statusFilter, selectedGrowerId, selectedFarmId, searchTerm]);

  // 3. Filtered ready cycles
  const filteredReadyCycles = useMemo(() => {
    return yearReadyCycles.filter((c) => {
      // Grower filter
      if (selectedGrowerId && c.grower_id !== selectedGrowerId) {
        return false;
      }

      // Farm filter
      if (selectedFarmId && c.farm_id !== selectedFarmId) {
        return false;
      }

      // Search term
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const cycleCode = (c.cycle_code || '').toLowerCase();
        const batchCode = (c.batch_code || '').toLowerCase();
        const growerName = (c.growers?.full_name || c.legacy_raiser_name || '').toLowerCase();
        const farmName = (c.farms?.farm_name || '').toLowerCase();

        return (
          cycleCode.includes(term) ||
          batchCode.includes(term) ||
          growerName.includes(term) ||
          farmName.includes(term)
        );
      }

      return true;
    });
  }, [yearReadyCycles, selectedGrowerId, selectedFarmId, searchTerm]);

  // 4. Annual Herd & Settlement KPIs
  const kpis = useMemo(() => {
    const readyCount = yearReadyCycles.length;
    const drafts = yearSettlements.filter((s) => s.status === 'draft' || s.status === 'approved');
    const finalized = yearSettlements.filter((s) => s.status === 'frozen' || (s.status as string) === 'finalized');
    const closed = yearSettlements.filter((s) => (s.status as string) === 'closed');
    const totalFrozenPayout = finalized.reduce((sum, s) => sum + Number(s.grower_net_payout || 0), 0);

    return {
      readyCount,
      draftCount: drafts.length,
      frozenCount: finalized.length,
      closedCount: closed.length,
      totalFrozenPayout,
    };
  }, [yearSettlements, yearReadyCycles]);

  // Options for pickers
  const growerOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    settlements.forEach((s) => {
      if (s.grower_id && s.growers?.full_name && !seen.has(s.grower_id)) {
        seen.add(s.grower_id);
        opts.push({ value: s.grower_id, label: s.growers.full_name });
      }
    });
    readyCycles.forEach((c) => {
      if (c.grower_id && c.growers?.full_name && !seen.has(c.grower_id)) {
        seen.add(c.grower_id);
        opts.push({ value: c.grower_id, label: c.growers.full_name });
      }
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [settlements, readyCycles]);

  const farmOptions = useMemo(() => {
    return farms.map((f) => ({
      value: f.id,
      label: `${f.farm_name}${f.municipality_city ? ` (${f.municipality_city})` : ''}`,
    }));
  }, [farms]);

  const statusTabItems = [
    { key: 'all', label: `All (${yearSettlements.length + yearReadyCycles.length})` },
    { key: 'active', label: `Active (${yearReadyCycles.length})` },
    { key: 'draft', label: `Draft (${kpis.draftCount})` },
    { key: 'finalized', label: `Finalized (${kpis.frozenCount})` },
    { key: 'closed', label: `Closed (${kpis.closedCount})` },
  ];

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('active');
    setSelectedGrowerId('');
    setSelectedFarmId('');
  };

  const isFiltered = searchTerm !== '' || statusFilter !== 'active' || selectedGrowerId !== '' || selectedFarmId !== '';

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* 1. Standard PageHeader with dynamic YearSelector */}
      <PageHeader
        title={selectedYear === 'all' ? 'Grower Settlements & Liquidation' : `Grower Settlements — ${selectedYear}`}
        subtitle="Interactive cycle profit sharing, adjustments manager, and finalized snapshot liquidation."
        icon={Scale}
        helpScreenKey="settlements"
        yearSelector={
          <YearSelector
            availableYears={availableYears}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        }
        actions={
          <ActionTooltip content="Reload grower settlement drafts, approved payouts, and finalized archives.">
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              disabled={loading}
              className="text-xs h-9 min-h-[44px] gap-1.5"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </ActionTooltip>
        }
      />

      {/* Error Alert Banner */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between gap-3 text-red-800 text-sm">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-semibold">Unable to load settlement records</p>
              <p className="text-xs text-red-700 mt-0.5">{error}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="bg-white hover:bg-red-50 text-red-700 border-red-300 text-xs font-semibold min-h-[44px]"
          >
            Retry
          </Button>
        </div>
      )}

      {/* 2. Standard 4-Card KpiCardGroup: Canonical Lifecycle */}
      <KpiCardGroup columns={4}>
        <KpiCard
          title="Active"
          value={`${kpis.readyCount} Active`}
          subtitle="Cycle open, no draft started"
          icon={Clock}
          variant="emerald"
        />
        <KpiCard
          title="Draft"
          value={`${kpis.draftCount} Draft`}
          subtitle="Settlement started & editable"
          icon={FileSpreadsheet}
          variant="amber"
        />
        <KpiCard
          title="Finalized"
          value={`${kpis.frozenCount} Finalized`}
          subtitle="Settlement completed & locked"
          icon={Lock}
          variant="indigo"
        />
        <KpiCard
          title="Closed"
          value={`${kpis.closedCount} Closed`}
          subtitle="Finalized cycle closed"
          icon={CheckCircle2}
          variant="default"
        />
      </KpiCardGroup>

      {/* 3. Filter Controls: Status Tabs + FilterBar */}
      <div className="space-y-3">
        {/* Status Lifecycle Segmented Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
          {statusTabItems.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors whitespace-nowrap ${
                statusFilter === tab.key
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* FilterBar with Search and Comboboxes */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search cycle, batch, grower, farm..."
          onReset={handleResetFilters}
          showReset={isFiltered}
        >
          <div className="w-full sm:w-56">
            <Combobox
              options={growerOptions}
              value={selectedGrowerId}
              onChange={setSelectedGrowerId}
              allowAll={true}
              allLabel="All Raisers / Growers"
              placeholder="Filter by Raiser..."
              searchPlaceholder="Search grower..."
            />
          </div>

          <div className="w-full sm:w-52">
            <Combobox
              options={farmOptions}
              value={selectedFarmId}
              onChange={setSelectedFarmId}
              allowAll={true}
              allLabel="All Farm Locations"
              placeholder="Filter by Farm..."
              searchPlaceholder="Search farm..."
            />
          </div>
        </FilterBar>
      </div>

      {/* 4. Desktop Table & Mobile Cards */}
      {loading ? (
        <LoadingState message="Loading grower settlement records..." />
      ) : (statusFilter === 'active' || statusFilter === 'ready_to_settle') ? (
        /* --- Active Cycles View --- */
        <div>
          {/* Desktop Table (md:table) */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-900">
                  Active Production Cycles ({filteredReadyCycles.length})
                </h2>
                <p className="text-xs text-slate-500">
                  Cycles open without a started settlement draft.
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100/70 text-slate-700 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-3.5">Cycle & Batch</th>
                    <th className="py-3 px-3.5">Grower & Farm</th>
                    <th className="py-3 px-3.5">Cycle Status</th>
                    <th className="py-3 px-3.5">Start Date</th>
                    <th className="py-3 px-3.5">Contract Split</th>
                    <th className="py-3 px-3.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {filteredReadyCycles.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-3.5 font-bold font-mono text-slate-900">
                        <div>{c.cycle_code}</div>
                        <div className="text-[11px] text-slate-500 font-mono">Batch: {c.batch_code}</div>
                      </td>
                      <td className="py-3 px-3.5">
                        <div className="font-semibold text-slate-900">
                          {c.growers?.full_name || c.legacy_raiser_name || 'Grower Partner'}
                        </div>
                        <div className="text-[11px] text-slate-500">{c.farms?.farm_name || 'Farm'}</div>
                      </td>
                      <td className="py-3 px-3.5">
                        <StatusBadge status={c.status} category="cycle" />
                      </td>
                      <td className="py-3 px-3.5 text-slate-600 font-mono">
                        {formatDate(c.start_date)}
                      </td>
                      <td className="py-3 px-3.5 font-mono text-slate-700">
                        {c.grower_contracts
                          ? `${c.grower_contracts.grower_share_pct}% Grower / ${c.grower_contracts.vsb_share_pct}% VSB`
                          : '50% / 50% (Default)'}
                      </td>
                      <td className="py-3 px-3.5 text-right">
                        <Button
                          size="sm"
                          onClick={() => navigate(`/cycles/${c.id}/settlement`)}
                          className="h-8 px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-xs"
                        >
                          Open Workspace <ChevronRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filteredReadyCycles.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-400">
                        No production cycles currently match the criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards (md:hidden) */}
          <div className="md:hidden space-y-3">
            {filteredReadyCycles.map((c) => (
              <div
                key={c.id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-mono font-bold text-sm text-slate-900">{c.cycle_code}</div>
                    <div className="text-xs text-slate-500">Batch: {c.batch_code}</div>
                  </div>
                  <StatusBadge status={c.status} category="cycle" />
                </div>

                <div className="text-xs space-y-1 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Grower:</span>
                    <span className="font-semibold text-slate-900">
                      {c.growers?.full_name || c.legacy_raiser_name || 'Grower Partner'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Farm:</span>
                    <span className="text-slate-700">{c.farms?.farm_name || 'Farm'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Contract Split:</span>
                    <span className="font-mono font-semibold text-slate-800">
                      {c.grower_contracts
                        ? `${c.grower_contracts.grower_share_pct}% / ${c.grower_contracts.vsb_share_pct}%`
                        : '50% / 50%'}
                    </span>
                  </div>
                </div>

                <Button
                  size="sm"
                  onClick={() => navigate(`/cycles/${c.id}/settlement`)}
                  className="w-full h-11 min-h-[44px] bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold justify-center gap-1"
                >
                  Open Settlement Workspace <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            ))}

            {filteredReadyCycles.length === 0 && (
              <div className="p-8 text-center bg-white rounded-xl border border-slate-200 text-slate-400">
                No production cycles ready to settle for the selected criteria.
              </div>
            )}
          </div>
        </div>
      ) : (
        /* --- Standard Settlements Table View --- */
        <div>
          {/* Desktop Table (md:table) */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100/70 text-slate-700 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-3.5">Settlement & Status</th>
                    <th className="py-3 px-3.5">Cycle & Batch</th>
                    <th className="py-3 px-3.5">Grower & Farm Location</th>
                    <th className="py-3 px-3.5 text-right">Gross Sales (₱)</th>
                    <th className="py-3 px-3.5 text-right">Production Costs (₱)</th>
                    <th className="py-3 px-3.5 text-right">Net Profit (₱)</th>
                    <th className="py-3 px-3.5 text-right">Grower Net Payout (₱)</th>
                    <th className="py-3 px-3.5 text-center w-36">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {filteredSettlements.map((s) => {
                    const cycleCode = s.production_cycles?.cycle_code || 'CYCLE';
                    const batchCode = s.production_cycles?.batch_code || 'BATCH';
                    const growerName = s.growers?.full_name || s.legacy_raiser_name || 'Grower Partner';
                    const farmName = s.production_cycles?.farms?.farm_name || 'Sorosoro Ibaba';
                    const totalCosts =
                      Number(s.total_feed_cost || 0) +
                      Number(s.total_piglet_cost || 0) +
                      Number(s.total_med_cost || 0) +
                      Number(s.total_other_costs || 0);

                    return (
                      <tr key={s.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-3.5">
                          <div className="font-mono font-bold text-slate-900">
                            {s.settlement_number || 'STL-DRAFT'}
                          </div>
                          <div className="mt-1">
                            <StatusBadge status={s.status} category="settlement" />
                          </div>
                        </td>
                        <td className="py-3 px-3.5">
                          <div className="font-bold text-slate-900">{cycleCode}</div>
                          <div className="text-[11px] text-slate-500 font-mono">{batchCode}</div>
                        </td>
                        <td className="py-3 px-3.5">
                          <div className="font-semibold text-slate-900">{growerName}</div>
                          <div className="text-[11px] text-slate-500">{farmName}</div>
                        </td>
                        <td className="py-3 px-3.5 text-right font-mono text-slate-800">
                          {formatCurrency(s.total_sales_revenue)}
                        </td>
                        <td className="py-3 px-3.5 text-right font-mono text-slate-800">
                          {formatCurrency(totalCosts)}
                        </td>
                        <td className="py-3 px-3.5 text-right font-mono font-semibold text-slate-900">
                          {formatCurrency(s.gross_profit)}
                        </td>
                        <td className="py-3 px-3.5 text-right font-mono font-bold text-indigo-900 text-xs">
                          {formatCurrency(s.grower_net_payout)}
                        </td>
                        <td className="py-3 px-3.5 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <ActionTooltip content="Open settlement calculator, verify production costs, and manage adjustments.">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/cycles/${s.cycle_id}/settlement`)}
                                className="h-8 px-2.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50 text-xs font-semibold shadow-xs"
                              >
                                Workspace
                              </Button>
                            </ActionTooltip>
                            <ActionTooltip content="Generate formal grower liquidation statement for review and signature.">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/cycles/${s.cycle_id}/settlement?print=1`)}
                                className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900"
                              >
                                <Printer className="w-4 h-4 text-indigo-600" />
                              </Button>
                            </ActionTooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filteredSettlements.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-slate-400">
                        <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p className="font-semibold text-slate-600">No settlement records found</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Select "Active" to begin a new settlement draft.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards (md:hidden) */}
          <div className="md:hidden space-y-3">
            {filteredSettlements.map((s) => {
              const cycleCode = s.production_cycles?.cycle_code || 'CYCLE';
              const batchCode = s.production_cycles?.batch_code || 'BATCH';
              const growerName = s.growers?.full_name || s.legacy_raiser_name || 'Grower Partner';
              const farmName = s.production_cycles?.farms?.farm_name || 'Sorosoro Ibaba';
              const totalCosts =
                Number(s.total_feed_cost || 0) +
                Number(s.total_piglet_cost || 0) +
                Number(s.total_med_cost || 0) +
                Number(s.total_other_costs || 0);

              return (
                <div
                  key={s.id}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-mono font-bold text-sm text-slate-900">
                        {s.settlement_number || 'STL-DRAFT'}
                      </div>
                      <div className="text-xs text-slate-500 font-mono">
                        {cycleCode} • {batchCode}
                      </div>
                    </div>
                    <StatusBadge status={s.status} category="settlement" />
                  </div>

                  <div className="text-xs space-y-1.5 bg-slate-50 p-3 rounded-lg border border-slate-100">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Grower & Farm:</span>
                      <span className="font-semibold text-slate-900 text-right">
                        {growerName} ({farmName})
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Gross Sales:</span>
                      <span className="font-mono font-medium text-slate-800">
                        {formatCurrency(s.total_sales_revenue)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Production Costs:</span>
                      <span className="font-mono text-slate-700">{formatCurrency(totalCosts)}</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-200 pt-1">
                      <span className="text-slate-600 font-medium">Net Profit:</span>
                      <span className="font-mono font-semibold text-slate-900">
                        {formatCurrency(s.gross_profit)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-slate-200 pt-1">
                      <span className="font-bold text-indigo-900">Grower Net Payout:</span>
                      <span className="font-mono font-bold text-indigo-900 text-sm">
                        {formatCurrency(s.grower_net_payout)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/cycles/${s.cycle_id}/settlement`)}
                      className="flex-1 h-11 min-h-[44px] bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold justify-center gap-1"
                    >
                      Workspace <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/cycles/${s.cycle_id}/settlement?print=1`)}
                      className="h-11 min-h-[44px] px-3 text-slate-700 border-slate-300"
                      title="Print Statement"
                    >
                      <Printer className="w-4 h-4 text-indigo-600" />
                    </Button>
                  </div>
                </div>
              );
            })}

            {filteredSettlements.length === 0 && (
              <div className="p-8 text-center bg-white rounded-xl border border-slate-200 text-slate-400">
                <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="font-semibold text-slate-600">No settlement records found</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Select "Active" to begin a new settlement draft.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
